import React, { useState, useCallback } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import { useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import { adminWorkerApi, getApiErrorMessage } from '../services/api';
import type { WorkerImportResponse, WorkerImportRowResult } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';

const PRIMARY_BLUE = '#0071e3';
const ALERT_BG = '#fff4e0';

const STATUS_META: Record<WorkerImportRowResult['status'], { label: string; color: string; bg: string }> = {
  created: { label: '新規登録', color: '#1e7e34', bg: '#e6f4ea' },
  alert: { label: '要確認', color: '#b26a00', bg: ALERT_BG },
};

const CrewBulkImportPage: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useAuth();
  const { managementToken } = useManagementAuth();
  const token = managementToken || localStorage.getItem('management_token') || '';

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [result, setResult] = useState<WorkerImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleDownloadTemplate = useCallback(async () => {
    if (!storeId) return;
    setError(null);
    try {
      const blob = await adminWorkerApi.importTemplate(storeId, token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'worker_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(getApiErrorMessage(err, 'テンプレートの取得に失敗しました'));
    }
  }, [storeId, token]);

  const handleUpload = useCallback(async () => {
    if (!storeId || !file) return;
    setIsUploading(true);
    setError(null);
    setResult(null);
    try {
      const res = await adminWorkerApi.importCsv(storeId, file, token);
      setResult(res);
    } catch (err) {
      setError(getApiErrorMessage(err, 'CSVの取込に失敗しました'));
    } finally {
      setIsUploading(false);
    }
  }, [storeId, file, token]);

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <PageHeader
        title="クルーCSV一括登録"
        icon={<GroupAddIcon />}
        description="各行を新規クルーとして一括登録します。本人が店舗の出勤QRコードからLINE登録すると自動で連携されます"
        onBack={() => navigate('/crew-management')}
      />

      <Card sx={{ mb: 2, borderRadius: '14px' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            1. テンプレートをダウンロード
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 1.5 }}>
            姓・名・生年月日・電話番号・メールアドレス・メモ・時給 の列で作成してください。
            「時給」は任意です（入力すると、この店舗での飛び込み出勤の勤怠に自動適用され、承認時の時給入力が不要になります）。
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ textTransform: 'none' }}
          >
            テンプレートCSVをダウンロード
          </Button>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2, borderRadius: '14px' }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            2. CSVをアップロード
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ textTransform: 'none' }}
            >
              ファイルを選択
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => {
                  setFile(e.target.files?.[0] ?? null);
                  setResult(null);
                }}
              />
            </Button>
            {file && (
              <Typography variant="body2" sx={{ color: '#333' }}>
                {file.name}
              </Typography>
            )}
            <Button
              variant="contained"
              onClick={handleUpload}
              disabled={!file || isUploading}
              sx={{ bgcolor: PRIMARY_BLUE, textTransform: 'none', boxShadow: 'none', ml: 'auto' }}
            >
              {isUploading ? '取込中…' : '取り込む'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isUploading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      )}

      {result && (
        <Card sx={{ borderRadius: '14px' }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              3. 取込結果
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              <Chip label={`合計 ${result.total}`} size="small" />
              <Chip label={`新規 ${result.created_count}`} size="small" sx={{ bgcolor: '#e6f4ea', color: '#1e7e34' }} />
              <Chip label={`要確認 ${result.alert_count}`} size="small" sx={{ bgcolor: ALERT_BG, color: '#b26a00' }} />
            </Box>
            {result.alert_count > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                「要確認」の行は必須項目の不備、または既に登録済みのクルーとの重複（メールアドレス、
                もしくは電話番号＋生年月日が一致）です。内容をご確認のうえ、修正して再取込してください。
              </Alert>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>行</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>氏名</TableCell>
                    <TableCell>備考</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result.rows.map((r) => {
                    const meta = STATUS_META[r.status];
                    return (
                      <TableRow key={r.row} sx={{ bgcolor: r.status === 'alert' ? ALERT_BG : undefined }}>
                        <TableCell>{r.row}</TableCell>
                        <TableCell>
                          <Chip
                            label={meta.label}
                            size="small"
                            sx={{ bgcolor: meta.bg, color: meta.color, fontSize: '0.7rem', height: 20 }}
                          />
                        </TableCell>
                        <TableCell>{r.name || '-'}</TableCell>
                        <TableCell sx={{ color: '#666', fontSize: '0.8rem' }}>{r.error || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default CrewBulkImportPage;
